import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { useGetAdminDealRedemptions } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Download } from "lucide-react";

function fmt(ts: string) {
  return new Date(ts).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function AdminDealRedemptions() {
  const { user, isLoading: authLoading } = useAuth();
  const [search, setSearch] = useState("");

  const { data, isLoading } = useGetAdminDealRedemptions({ limit: 500 });

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  const items = (data?.items ?? []).filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.dealTitle?.toLowerCase().includes(q) ||
      r.dealBusinessName?.toLowerCase().includes(q) ||
      r.userName?.toLowerCase().includes(q) ||
      r.userEmail?.toLowerCase().includes(q)
    );
  });

  const downloadCsv = () => {
    const rows = [
      ["ID", "Deal", "Business", "User", "Email", "Redeemed At"],
      ...(data?.items ?? []).map((r) => [
        r.id, r.dealTitle ?? "", r.dealBusinessName ?? "", r.userName ?? "", r.userEmail ?? "", r.redeemedAt,
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "deal-redemptions.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout current="/admin/deal-redemptions">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-primary">Deal Redemptions</h1>
            <p className="text-sm text-muted-foreground mt-1">{data?.total ?? 0} total redemptions across all deals</p>
          </div>
          <button
            onClick={downloadCsv}
            className="flex items-center gap-2 text-sm font-medium text-slate-600 border border-slate-200 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>

        <Input
          placeholder="Search by deal, business, or user…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />

        {isLoading ? (
          <div className="text-muted-foreground text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <p className="text-4xl mb-3">🏷️</p>
              <p className="text-muted-foreground">{search ? "No results match your search." : "No redemptions yet."}</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Deal</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Business</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">User</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Email</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Redeemed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((r, i) => (
                      <tr key={r.id} className={`border-b border-slate-50 ${i % 2 === 0 ? "" : "bg-slate-50/50"}`}>
                        <td className="px-4 py-3 font-medium text-slate-800">{r.dealTitle ?? "—"}</td>
                        <td className="px-4 py-3 text-slate-600">{r.dealBusinessName ?? "—"}</td>
                        <td className="px-4 py-3 text-slate-700">{r.userName ?? "—"}</td>
                        <td className="px-4 py-3 text-slate-500">{r.userEmail ?? "—"}</td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmt(r.redeemedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
